"use client"

import type { TrackEventProperties } from "@/modules/tracking/types"
import type { ElementType, FunctionComponent, HTMLAttributes, PropsWithChildren } from "react"

import { useEffect, useRef, useState } from "react"

import { useTracking } from "@/modules/tracking/useTracking"
import { useIntersectionObserver } from "@/modules/window/hooks/useIntersectionObserver"

type TrackObserverProps = PropsWithChildren<{
  observerOptions?: IntersectionObserverInit
  tagName?: ElementType
  trackingProps: TrackEventProperties | TrackEventProperties[]
}> &
  Partial<HTMLAttributes<HTMLElement>>

export const TrackObserver: FunctionComponent<TrackObserverProps> = ({
  trackingProps,
  observerOptions,
  children,
  tagName: TagName = "div",
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const tracking = useTracking()
  const [refIsLoaded, setRefIsLoaded] = useState(false)
  const [isAlreadyTracked, setIsAlreadyTracked] = useState(false)

  const isObserved = useIntersectionObserver(ref, observerOptions)

  useEffect(() => {
    setRefIsLoaded(ref.current !== null)

    if (refIsLoaded && isObserved && !isAlreadyTracked) {
      if (Array.isArray(trackingProps)) {
        trackingProps.forEach(({ eventName, eventProperties }) => {
          tracking?.track(eventName, eventProperties)
        })
      } else {
        const { eventName, eventProperties } = trackingProps
        tracking?.track(eventName, eventProperties)
      }
      setIsAlreadyTracked(Boolean(tracking))
    }
  }, [isAlreadyTracked, setIsAlreadyTracked, isObserved, tracking, trackingProps, ref, refIsLoaded, setRefIsLoaded])

  return (
    <TagName {...props} ref={ref}>
      {children}
    </TagName>
  )
}
